import { useState, useMemo, useEffect, useContext, useCallback } from "react";
import Icon from "../icon";
import UserContext from "../../context/user";
import { useQuery } from "@tanstack/react-query";
import ChoicePoints from "../choice-points";
import useCheckUpUpdateMutation from "../../hooks/mutations/useCheckupUpdateMutation";
import CheckUpContext from "../../context/check-up";
import CheckUpDiscardRequestModal from "./check-up-discard-request-modal";
import {
  getNotProvidedExams,
  getPackagePayloadFromRequest,
  getQuestionId,
} from "../../utils/check-up";

const CheckUpStep3 = ({ discardModal, setDiscardModal }) => {
  const { logged, callApi } = useContext(UserContext);
  const { labels, survey, formData, handlePrevious, currentRequest } = useContext(CheckUpContext);

  const clinicQuestionId = useMemo(() => getQuestionId(survey, "center"), [survey]);

  const [validationError, setValidationError] = useState();
  const [selected, setSelected] = useState();

  const {
    isFetching,
    data: clinics,
    isError: isClinicError,
  } = useQuery(["clinics"], () => callApi(`/checkUp/clinic/search?offset=0&limit=100`), {
    enabled: logged,
    select: (data) =>
      data?.list?.map((clinic) => ({
        ...clinic,
        notProvidedExams: getNotProvidedExams(currentRequest, clinic),
      })),
  });

  useEffect(() => {
    if (formData?.[clinicQuestionId]) {
      setSelected(formData[clinicQuestionId]);
    }
  }, [clinicQuestionId, formData]);

  const { mutateAsync: updateRequest, isError } = useCheckUpUpdateMutation();

  const handleSubmit = useCallback(async () => {
    if (selected && clinicQuestionId) {
      await updateRequest({
        survey,
        request: currentRequest,
        formData,
        values: { [clinicQuestionId]: Number(selected) },
        email: formData[getQuestionId(survey, "mail")],
        phone: formData[getQuestionId(survey, "phone")],
        gender: formData[getQuestionId(survey, "gender")],
        dateOfBirth: formData[getQuestionId(survey, "birthdate")],
        workplace: formData[getQuestionId(survey, "workPlace")],
        birthPlace: formData[getQuestionId(survey, "birthPlace")],
        taxCode: formData[getQuestionId(survey, "fiscalCode")],
        preferredTimeSlot: formData[getQuestionId(survey, "availability")],
        packageSelected: getPackagePayloadFromRequest(currentRequest),
        quadro: formData[getQuestionId(survey, "quadro")],
        manager: formData[getQuestionId(survey, "manager")],
        clinicId: Number(selected),
        lastCompletedStep: 2,
      });
    } else {
      setValidationError(true);
    }
  }, [clinicQuestionId, survey, formData, selected, currentRequest, updateRequest]);

  return isClinicError ? (
    <div className="error text-center">Error retrieving clinics</div>
  ) : (
    <>
      {isFetching ? (
        <div className="loading-spinner" />
      ) : (
        <>
          <ChoicePoints
            labels={labels}
            pickUpPoints={clinics}
            selected={selected}
            setSelected={setSelected}
            clinic={true}
          />
          <div className="form__buttons-wrapper form__buttons-wrapper--static">
            {validationError && (
              <div className="error text-center">{labels?.THIS_FIELD_IS_MANDATORY}</div>
            )}
            {isError && (
              <p className="form__error text-center">
                {labels?.WELFARE_BORSE_ERROR_SAVING_REQUEST || "Error saving requests"}
              </p>
            )}
            <div className="form__buttons form__buttons--center">
              <button className="link" onClick={handlePrevious} type="button">
                <Icon name="angle-down" />
                {labels?.WELFARE_FORM_BTN_PREVIOUS || "Previous"}
              </button>
              <button className="button primary form__submit" onClick={handleSubmit}>
                {labels?.WELFARE_FORM_BTN_NEXT || "Next"}
              </button>
            </div>
          </div>
        </>
      )}
      <CheckUpDiscardRequestModal
        key={!!discardModal}
        discardModal={discardModal}
        setDiscardModal={setDiscardModal}
        formWatch={{ ...formData, [clinicQuestionId]: Number(selected) || null }}
        lastCompletedStep={1}
      />
    </>
  );
};

export default CheckUpStep3;
